<template>
    <div class="grid grid-cols-3 gap-4">
        <template v-for="cell in cells" :key="cell.key">
            <UFormGroup v-if="cell.bp" label="BP" :error="bpError(cell.bp)">
                <div class="flex items-center gap-2">
                    <UInput v-model="local[cell.bp.systolic.key]" type="text" inputmode="decimal" aria-label="Systolic" class="flex-1"
                        :disabled="disabled" @blur="commit()" />
                    <span class="text-gray-400">/</span>
                    <UInput v-model="local[cell.bp.diastolic.key]" type="text" inputmode="decimal" aria-label="Diastolic" class="flex-1"
                        :disabled="disabled" @blur="commit()">
                        <template #trailing>
                            <span class="text-xs text-gray-400">{{ cell.bp.diastolic.unit }}</span>
                        </template>
                    </UInput>
                </div>
            </UFormGroup>
            <UFormGroup v-else-if="cell.field" :label="titleCase(cell.field.label)" :error="fieldError(cell.field)">
                <UInput v-model="local[cell.field.key]" type="text" inputmode="decimal" :disabled="disabled" @blur="commit()">
                    <template #trailing>
                        <span class="text-xs text-gray-400">{{ cell.field.unit }}</span>
                    </template>
                </UInput>
            </UFormGroup>
        </template>
    </div>
</template>

<script setup lang="ts">
import { VITAL_FIELDS, type VitalField, type VitalKey, type Vitals } from '~/types/fieldPost'
import { nonNumericVitals, sanitizeVitals, vitalErrors } from '~/utils/fieldPosts'

const props = defineProps<{
    modelValue: Vitals
    keys?: VitalKey[]
    disabled?: boolean
}>()

const emit = defineEmits<{ commit: [Vitals] }>()

const fields = computed(() => VITAL_FIELDS.filter(f => !props.keys || props.keys.includes(f.key)))

type BpPair = { systolic: VitalField; diastolic: VitalField }
type Cell = { key: string; field?: VitalField; bp?: BpPair }

const cells = computed<Cell[]>(() => {
    const systolic = fields.value.find(f => f.key === 'bp_systolic')
    const diastolic = fields.value.find(f => f.key === 'bp_diastolic')
    const pair = systolic && diastolic ? { systolic, diastolic } : undefined
    return fields.value.flatMap<Cell>((field) => {
        if (pair && field.key === 'bp_systolic') return [{ key: 'bp', bp: pair }]
        if (pair && field.key === 'bp_diastolic') return []
        return [{ key: field.key, field }]
    })
})

const local = reactive<Record<string, string>>({})
const invalid = ref<VitalKey[]>([])
const nonNumeric = ref<VitalKey[]>([])

function titleCase(label: string) {
    return label.replace(/\b[a-z]/g, c => c.toUpperCase())
}

function fieldError(field: VitalField, name?: string) {
    if (nonNumeric.value.includes(field.key)) return name ? `${name} must be a number.` : 'Enter a number.'
    if (invalid.value.includes(field.key)) return `${name ? `${name} must` : 'Must'} be ${field.min} to ${field.max}.`
    return undefined
}

function bpError(bp: BpPair) {
    return [fieldError(bp.systolic, 'Systolic'), fieldError(bp.diastolic, 'Diastolic')].filter(Boolean).join(' ') || undefined
}

watch(() => props.modelValue, (value) => {
    for (const field of VITAL_FIELDS) {
        local[field.key] = value?.[field.key] !== undefined ? String(value[field.key]) : ''
    }
}, { immediate: true })

function commit() {
    nonNumeric.value = nonNumericVitals(local)
    if (nonNumeric.value.length) return
    const vitals = sanitizeVitals(local)
    invalid.value = vitalErrors(vitals)
    if (invalid.value.length) return
    if (JSON.stringify(vitals) === JSON.stringify(sanitizeVitals(props.modelValue ?? {}))) return
    emit('commit', vitals)
}
</script>
